/**
 * Géométrie sur la sphère et dans le plan. Fonctions pures, sans état.
 */
import type { LatLon, LatLonTuple, LocalXY, Ring } from './types';

const EARTH_RADIUS_KM = 6371;
const DEG = Math.PI / 180;

/** Longueur d'un degré de latitude sur la sphère utilisée par distanceKm. */
const KM_PER_DEGREE = EARTH_RADIUS_KM * DEG;

/** Distance orthodromique entre deux points, en kilomètres. */
export function distanceKm(aLat: number, aLon: number, bLat: number, bLon: number): number {
  const dLat = (bLat - aLat) * DEG;
  const dLon = (bLon - aLon) * DEG;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(aLat * DEG) * Math.cos(bLat * DEG) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(h));
}

/** Ramène un angle dans l'intervalle [0, 360[. */
export function normalizeDeg(deg: number): number {
  return ((deg % 360) + 360) % 360;
}

/** Relèvement initial de a vers b, en degrés depuis le nord. */
export function bearingDeg(aLat: number, aLon: number, bLat: number, bLon: number): number {
  const dLon = (bLon - aLon) * DEG;
  const y = Math.sin(dLon) * Math.cos(bLat * DEG);
  const x =
    Math.cos(aLat * DEG) * Math.sin(bLat * DEG) -
    Math.sin(aLat * DEG) * Math.cos(bLat * DEG) * Math.cos(dLon);
  return normalizeDeg(Math.atan2(y, x) / DEG);
}

/**
 * Écart angulaire absolu entre deux caps, de 0 à 180°.
 * Gère correctement le passage par le nord : 350° et 10° sont à 20° l'un
 * de l'autre, et non à 340°.
 */
export function angleDeltaDeg(a: number, b: number): number {
  return Math.abs(((a - b + 540) % 360) - 180);
}

export interface LocalFrame {
  readonly origin: LatLon;
  /** Projette un point géographique en kilomètres depuis l'origine. */
  toLocal(lat: number, lon: number): LocalXY;
  /** Opération inverse, pour tracer des formes calculées en kilomètres. */
  toLatLon(x: number, y: number): LatLonTuple;
}

/**
 * Repère plat local, en kilomètres, centré sur un point.
 *
 * Suffisamment exact jusqu'à quelques centaines de kilomètres, et bien plus
 * simple à raisonner qu'une projection complète. Toute la géométrie du
 * panache vit dans ce repère.
 */
export function localFrame(origin: LatLon): LocalFrame {
  /*
   * Les deux facteurs d'échelle sont dérivés du MÊME rayon terrestre que
   * distanceKm.
   *
   * La version précédente mélangeait deux modèles : des constantes
   * ellipsoïdales prises à l'équateur (110,54 km par degré de latitude et
   * 111,32 en longitude) avec une distance calculée sur une sphère de
   * 6371 km. L'écart, 0,6 %, était systématique et visible : la fiche d'un
   * feu annonçait 15 km là où le texte du panache en comptait 14,9.
   */
  const kmPerDegreeLon = KM_PER_DEGREE * Math.cos(origin.lat * DEG);
  return {
    origin,
    toLocal: (lat, lon) => ({
      x: (lon - origin.lon) * kmPerDegreeLon,
      y: (lat - origin.lat) * KM_PER_DEGREE,
    }),
    toLatLon: (x, y) => [
      origin.lat + y / KM_PER_DEGREE,
      origin.lon + x / kmPerDegreeLon,
    ],
  };
}

/**
 * Médiane d'une série.
 *
 * Préférée à la moyenne pour les capteurs citoyens : ils ne sont pas
 * homologués, et un seul capteur déréglé déplacerait une moyenne alors
 * qu'il ne déplace pas une médiane.
 */
export function median(values: readonly number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = sorted.length >> 1;
  if (sorted.length % 2 === 1) return sorted[mid]!;
  return (sorted[mid - 1]! + sorted[mid]!) / 2;
}

/*
 * L'enveloppe convexe d'Andrew vivait ici. Elle a été RETIRÉE, volontairement,
 * et non remplacée par une variante : elle comblait tout creux du contour, donc
 * présentait comme brûlé ce qui n'avait jamais été détecté. Sur l'incendie du
 * massif landais, cela ajoutait plusieurs centaines de kilomètres carrés que
 * personne n'a mesurés. Voir footprintOutlines juste en dessous.
 */

/** Une détection, accompagnée de la taille de son empreinte quand on la connaît. */
export interface FootprintPoint extends LatLon {
  readonly footprintKm?: number;
}

/*
 * Les quatre directions, dans l'ordre trigonométrique. L'index sert à tourner :
 * (d + 1) % 4 est un quart de tour à gauche, (d + 3) % 4 un quart à droite.
 */
const DIRECTIONS = [
  [1, 0],
  [0, 1],
  [-1, 0],
  [0, -1],
] as const;

/**
 * Contours de l'union des empreintes satellite.
 *
 * Remplace l'enveloppe convexe, qui comblait tous les creux : elle peignait
 * comme brûlant l'espace entre deux détections éloignées, y compris ce qui
 * n'avait jamais été détecté. Sur l'incendie du massif landais, cela ajoutait
 * plusieurs centaines de kilomètres carrés que personne n'a mesurés.
 *
 * Ici, chaque détection couvre l'empreinte réelle de son pixel, 375 m pour
 * VIIRS et 1 km pour MODIS. On marque les cellules de grille qu'elle recouvre,
 * puis on trace le bord de l'ensemble obtenu. Rien n'est ajouté, rien n'est
 * comblé, et le résultat est reproductible : c'est de la géométrie, pas une
 * estimation.
 *
 * Conséquence assumée : un foyer discontinu rend plusieurs anneaux, parce qu'il
 * est réellement discontinu. Les plus grands d'abord.
 */
export function footprintOutlines(
  points: readonly FootprintPoint[],
  gridKm: number,
  defaultFootprintKm: number,
  maxRings: number,
): Ring[] {
  if (points.length === 0 || gridKm <= 0) return [];

  /*
   * L'origine du repère est arrondie au degré, jamais placée sur le barycentre
   * des détections. Sinon la grille se décale d'un balayage au suivant, dès que
   * le barycentre bouge de quelques mètres, et le contour d'un même feu se
   * redessine différemment à chaque rafraîchissement. Même raison que pour la
   * grille de balayage, qui est fixe elle aussi.
   */
  let sumLat = 0;
  let sumLon = 0;
  for (const point of points) {
    sumLat += point.lat;
    sumLon += point.lon;
  }
  const frame = localFrame({
    lat: Math.round(sumLat / points.length),
    lon: Math.round(sumLon / points.length),
  });

  const occupied = new Set<string>();
  for (const point of points) {
    const { x, y } = frame.toLocal(point.lat, point.lon);
    const half = (point.footprintKm ?? defaultFootprintKm) / 2;
    const fromX = Math.floor((x - half) / gridKm);
    const toX = Math.ceil((x + half) / gridKm) - 1;
    const fromY = Math.floor((y - half) / gridKm);
    const toY = Math.ceil((y + half) / gridKm) - 1;
    for (let ix = fromX; ix <= toX; ix += 1) {
      for (let iy = fromY; iy <= toY; iy += 1) {
        occupied.add(`${ix},${iy}`);
      }
    }
  }

  const isOccupied = (ix: number, iy: number): boolean => occupied.has(`${ix},${iy}`);

  /*
   * Une arête n'est gardée que si la cellule voisine est vide : les arêtes
   * partagées par deux cellules pleines s'annulent, et il ne reste que le bord
   * de l'union. Les quatre arêtes sont émises dans le sens trigonométrique,
   * l'intérieur toujours à gauche.
   */
  const outgoing = new Map<string, { dir: number; used: boolean }[]>();
  const addEdge = (i: number, j: number, dir: number): void => {
    const key = `${i},${j}`;
    const list = outgoing.get(key);
    if (list) list.push({ dir, used: false });
    else outgoing.set(key, [{ dir, used: false }]);
  };

  for (const cell of occupied) {
    const [rawX, rawY] = cell.split(',');
    const ix = Number(rawX);
    const iy = Number(rawY);
    if (!isOccupied(ix, iy - 1)) addEdge(ix, iy, 0);
    if (!isOccupied(ix + 1, iy)) addEdge(ix + 1, iy, 1);
    if (!isOccupied(ix, iy + 1)) addEdge(ix + 1, iy + 1, 2);
    if (!isOccupied(ix - 1, iy)) addEdge(ix, iy + 1, 3);
  }

  const takeEdge = (i: number, j: number, dir: number): boolean => {
    const list = outgoing.get(`${i},${j}`);
    if (!list) return false;
    for (const edge of list) {
      if (!edge.used && edge.dir === dir) {
        edge.used = true;
        return true;
      }
    }
    return false;
  };

  const rings: { ring: Ring; area: number; anchor: [number, number] }[] = [];

  for (const [startKey, list] of outgoing) {
    for (const first of list) {
      if (first.used) continue;
      first.used = true;

      const [rawX, rawY] = startKey.split(',');
      const startI = Number(rawX);
      const startJ = Number(rawY);

      /*
       * TOUS les sommets sont empilés, y compris celui de départ. Les omettre
       * pour ne garder que les changements de direction paraissait économique,
       * mais coupait le sommet initial du tracé quand la marche commençait sur
       * un coin : le contour d'un fer à cheval s'en trouvait refermé, et son
       * creux redevenait plein. Le lissage vient après, sur le cycle complet.
       */
      const walk: [number, number][] = [[startI, startJ]];
      let i = startI;
      let j = startJ;
      let dir = first.dir;

      for (;;) {
        const step = DIRECTIONS[dir]!;
        const nextI = i + step[0];
        const nextJ = j + step[1];
        if (nextI === startI && nextJ === startJ) break;

        /*
         * Priorité au virage à droite, puis tout droit, puis à gauche. Cette
         * règle sépare proprement deux amas qui ne se touchent que par un coin,
         * au lieu d'en faire un anneau croisé.
         */
        let nextDir = -1;
        for (const candidate of [(dir + 3) % 4, dir, (dir + 1) % 4]) {
          if (takeEdge(nextI, nextJ, candidate)) {
            nextDir = candidate;
            break;
          }
        }
        walk.push([nextI, nextJ]);
        if (nextDir === -1) break;
        i = nextI;
        j = nextJ;
        dir = nextDir;
      }

      if (walk.length < 4) continue;

      /*
       * Suppression des sommets alignés, sur le cycle entier et non sur la
       * séquence ouverte : la jointure du départ est traitée comme les autres.
       * Un front rectiligne de cent cellules devient deux sommets au lieu de
       * cent un, sans rien approximer, puisque seuls des points colinéaires
       * disparaissent.
       */
      const corners: [number, number][] = [];
      for (let k = 0; k < walk.length; k += 1) {
        const previous = walk[(k - 1 + walk.length) % walk.length]!;
        const current = walk[k]!;
        const next = walk[(k + 1) % walk.length]!;
        const turns =
          (current[0] - previous[0]) * (next[1] - current[1]) !==
          (current[1] - previous[1]) * (next[0] - current[0]);
        if (turns) corners.push(current);
      }

      if (corners.length < 3) continue;

      /*
       * Rotation jusqu'au sommet le plus petit. Sans cela, l'anneau dépendrait
       * de l'arête par laquelle la marche a commencé, donc de l'ordre d'arrivée
       * des tuiles satellite : le même feu se redessinerait différemment d'un
       * balayage au suivant.
       */
      let smallest = 0;
      for (let k = 1; k < corners.length; k += 1) {
        const candidate = corners[k]!;
        const best = corners[smallest]!;
        if (candidate[0] < best[0] || (candidate[0] === best[0] && candidate[1] < best[1])) {
          smallest = k;
        }
      }
      const ordered = [...corners.slice(smallest), ...corners.slice(0, smallest)];

      let twiceArea = 0;
      for (let k = 0; k < ordered.length; k += 1) {
        const current = ordered[k]!;
        const next = ordered[(k + 1) % ordered.length]!;
        twiceArea += current[0] * next[1] - next[0] * current[1];
      }

      rings.push({
        ring: ordered.map(([cx, cy]): LatLonTuple => frame.toLatLon(cx * gridKm, cy * gridKm)),
        area: Math.abs(twiceArea) / 2,
        anchor: ordered[0]!,
      });
    }
  }

  /*
   * Les plus vastes d'abord, et un plafond sur le nombre. Un foyer très
   * fragmenté peut produire des dizaines d'îlots, et tous les dessiner
   * coûterait plus qu'il n'apprend. Le plafond est explicite plutôt que
   * silencieux : voir ZONE_MAX_OUTLINE_RINGS.
   */
  return rings
    .sort(
      (a, b) =>
        b.area - a.area || a.anchor[0] - b.anchor[0] || a.anchor[1] - b.anchor[1],
    )
    .slice(0, maxRings)
    .map((entry) => entry.ring);
}

/**
 * Le point tombe-t-il dans l'un des anneaux ? Lancer de rayon.
 *
 * Sans anneau fourni on ne filtre rien, plutôt que de tout rejeter : une
 * donnée de contour manquante ne doit pas faire disparaître tous les feux.
 */
export function isInsideRings(lat: number, lon: number, rings: readonly Ring[]): boolean {
  if (rings.length === 0) return true;
  let inside = false;
  for (const ring of rings) {
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const [curLat, curLon] = ring[i]!;
      const [prevLat, prevLon] = ring[j]!;
      if (
        curLat > lat !== prevLat > lat &&
        lon < ((prevLon - curLon) * (lat - curLat)) / (prevLat - curLat) + curLon
      ) {
        inside = !inside;
      }
    }
  }
  return inside;
}
